import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ReloadIcon } from '@radix-ui/react-icons';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { confirmPasswordReset } from '@/services/passwordHandler';

const schema = yup
  .object({
    code: yup.string().required('Verification code is required'),
    password: yup
      .string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required.'),
  })
  .required();

const VerificationCode = () => {
  const { search } = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(search);
  const UserEmail = queryParams.get('email');

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async ({ code, password }) => {
    try {
      await confirmPasswordReset(UserEmail, code, password);
      history.push('/login');
    } catch (err) {
      setError('root', {
        message: err.message,
      });
    }
  };

  return (
    <div className="relative h-screen bg-zinc-700">
      <div
        className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
        style={{ backgroundImage: `url(/bg-new.jpg)` }}
      />

      <div className="flex flex-col h-full items-center justify-center relative z-10">
        <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
          <img
            alt="logo"
            src="/TriVista_Horizontal_Logo_White.png"
            className="w-[300px]"
          />
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="max-w-2xl flex flex-col items-center justify-center mx-8">
            <h1 className="text-3xl text-white text-center lg:tracking-[0.75rem] tracking-[0.5rem]">
              TRIVISTA INTELLIGENCE
            </h1>
            <div className="mt-12 text-white text-xl text-center">
              An email with a verification code has been sent to <b>{UserEmail}</b>. Enter the code below:
            </div>
            <Card className="mt-4 w-full mx-8 md:mx-auto md:w-auto px-4">
              {errors.root && (
                <Alert variant="destructive" className="mt-4 max-w-96">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{errors.root.message}</AlertDescription>
                </Alert>
              )}
              <div className="my-4">
                <Label className="text-xl">Code</Label>
                <Input
                  className="h-8 w-80 sm:w-auto sm:min-w-80"
                  {...register('code')}
                />
                {errors.code && (
                  <p className="text-red-500 mt-1">{errors.code.message}</p>
                )}
              </div>
              <div className="mt-4">
                <Label className="text-xl">Password</Label>
                <Input
                  className="h-8 w-80 sm:w-auto sm:min-w-80"
                  type="password"
                  {...register('password')}
                />
                {errors.password && (
                  <p className="text-red-500 mt-1">{errors.password.message}</p>
                )}
              </div>
              <Button
                className="my-4 w-full px-12 py-6 text-xl rounded-xl"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && <ReloadIcon className="w-6 h-6 animate-spin mr-2" />}
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Card>
          </div>
        </form>
        <Link to="/login" className="text-white mt-4 flex items-center gap-2">
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
      </div>
    </div>
  );
};

export default VerificationCode;
